import { useEffect, useState } from "react";
import {
  X,
  Plus,
  Trash,
  PencilSimple,
  Check,
  ArrowClockwise,
  SquaresFour,
  List as ListIcon,
  Certificate,
  House,
  ForkKnife,
  Car,
  Repeat,
  Lightning,
  GameController,
  Heart,
  GraduationCap,
  ShoppingBag,
  Airplane,
  Shield,
  Percent,
  ArrowsLeftRight,
  Tag,
  CurrencyDollar,
  Wallet,
  CreditCard,
  Gift,
  Book,
  Coffee,
  DeviceMobile,
  Baby,
  Dog,
  PawPrint,
  MusicNote,
  Barbell,
  FirstAid,
  Package,
  Wrench,
  Star,
  Sparkle,
  Sun,
  Palette,
  Camera,
  Bank,
  Buildings,
  Briefcase,
  Hammer,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";

// -------------- Icon library --------------
// Keep in a static map so we can render dynamically from the key.
export const CATEGORY_ICONS = {
  CurrencyDollar, House, ForkKnife, Car, Repeat, Lightning, GameController,
  Heart, GraduationCap, ShoppingBag, Airplane, Shield, Percent, ArrowsLeftRight,
  Tag, Wallet, CreditCard, Gift, Book, Coffee, DeviceMobile, Baby, Dog, PawPrint,
  MusicNote, Barbell, FirstAid, Package, Wrench, Star, Sparkle, Sun, Palette,
  Camera, Bank, Buildings, Briefcase, Hammer,
};

const ICON_CHOICES = Object.keys(CATEGORY_ICONS);

const COLOR_CHOICES = [
  "#10B981", "#059669", "#22C55E", "#84CC16",
  "#EAB308", "#FACC15", "#F59E0B", "#F97316",
  "#EF4444", "#DC2626", "#EC4899", "#D946EF",
  "#A855F7", "#8B5CF6", "#6366F1", "#3B82F6",
  "#2563EB", "#0EA5E9", "#06B6D4", "#14B8A6",
  "#64748B", "#94A3B8", "#78716C", "#111827",
];

export function CategoryIcon({ name, size = 14, weight = "fill", ...rest }) {
  const Comp = CATEGORY_ICONS[name] || Tag;
  return <Comp size={size} weight={weight} {...rest} />;
}

/**
 * Editable category manager. Full CRUD with color + icon.
 */
export default function CategoryManagerDialog({ open, onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ label: "", color: "#94A3B8", icon: "Tag" });
  const [newDraft, setNewDraft] = useState({ label: "", color: "#10B981", icon: "Tag" });
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() => {
    // Persist last-used view across dialog opens
    return window.localStorage.getItem("cfo_cat_view") || "grid";
  });

  const setViewPersisted = (v) => {
    setView(v);
    window.localStorage.setItem("cfo_cat_view", v);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/categories");
      setItems(data || []);
    } catch (err) {
      toast.error("No se pudieron cargar las categorías");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const startEdit = (cat) => {
    setEditingId(cat.category_id);
    setDraft({ label: cat.label, color: cat.color, icon: cat.icon });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ label: "", color: "#94A3B8", icon: "Tag" });
  };

  const saveEdit = async () => {
    if (!draft.label?.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/finance/categories/${editingId}`, {
        label: draft.label.trim(),
        color: draft.color,
        icon: draft.icon,
      });
      setItems((prev) => prev.map((c) => (c.category_id === editingId ? data : c)));
      toast.success("Categoría actualizada");
      cancelEdit();
      onChange?.();
    } catch (err) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeCategory = async (cat) => {
    const remainingKeys = items
      .filter((c) => c.category_id !== cat.category_id)
      .map((c) => c.key);
    const remap =
      window.prompt(
        `¿Eliminar la categoría "${cat.label}"?\n\nLas transacciones se moverán a otra categoría.\nEscribe una de:\n${remainingKeys.join(", ")}\n\n(Deja vacío para usar "other")`,
        "other",
      );
    if (remap === null) return;
    const target = (remap || "other").trim().toLowerCase();
    setSaving(true);
    try {
      const { data } = await api.delete(`/finance/categories/${cat.category_id}`, {
        data: { remap_to: target },
      });
      setItems((prev) => prev.filter((c) => c.category_id !== cat.category_id));
      toast.success(
        `Eliminada. ${data.transactions_remapped} transacción(es) movidas a "${data.remapped_to}"`,
      );
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setSaving(false);
    }
  };

  const createCategory = async () => {
    if (!newDraft.label?.trim()) {
      toast.error("Escribe un nombre");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/finance/categories", {
        label: newDraft.label.trim(),
        color: newDraft.color,
        icon: newDraft.icon,
      });
      setItems((prev) => [...prev, data]);
      toast.success(`Categoría "${data.label}" creada`);
      setNewDraft({ label: "", color: "#10B981", icon: "Tag" });
      setShowNew(false);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally {
      setSaving(false);
    }
  };

  const reseedBuiltins = async () => {
    if (!window.confirm("¿Restaurar las categorías predeterminadas que faltan?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/finance/categories/reseed-builtins");
      toast.success(`${data.inserted} categoría(s) restauradas`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error("No se pudo restaurar");
    } finally {
      setSaving(false);
    }
  };

  const seedIRS = async () => {
    if (!window.confirm("¿Agregar las 22 categorías del IRS Schedule C (Form 1040) para preparar taxes? Se agregan solo las que aún no tengas.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/finance/categories/seed-irs");
      if (data.inserted === 0) toast.info("Ya tienes todas las categorías del IRS");
      else toast.success(`${data.inserted} categoría(s) IRS agregada(s) · listas para Schedule C`);
      await load();
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo sembrar");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="category-manager-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-5 border-b border-white/8 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] flex items-center gap-1.5">
              <Tag size={12} weight="bold" /> GESTIÓN · CATEGORÍAS
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              Personaliza tus categorías
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              Renombra, cambia color/ícono o crea nuevas. Se aplican a todas tus transacciones.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Grid / List view toggle */}
            <div
              data-testid="category-view-toggle"
              className="inline-flex items-center p-0.5 rounded-lg bg-black/40 border border-white/10"
            >
              <button
                data-testid="category-view-grid"
                onClick={() => setViewPersisted("grid")}
                title="Vista de cuadrícula"
                className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                  view === "grid"
                    ? "on-solid bg-[#2563EB] text-white ring-2 ring-[#2563EB]/40"
                    : "text-white/50 hover:text-white hover:bg-white/6"
                }`}
              >
                <SquaresFour size={14} weight="fill" />
              </button>
              <button
                data-testid="category-view-list"
                onClick={() => setViewPersisted("list")}
                title="Vista de lista"
                className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                  view === "list"
                    ? "on-solid bg-[#2563EB] text-white ring-2 ring-[#2563EB]/40"
                    : "text-white/50 hover:text-white hover:bg-white/6"
                }`}
              >
                <ListIcon size={14} weight="bold" />
              </button>
            </div>
            <button
              onClick={onClose}
              data-testid="category-manager-close"
              className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="py-12 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={20} weight="bold" className="animate-spin mx-auto mb-2" />
              Cargando categorías...
            </div>
          ) : view === "grid" ? (
            <CategoryGridView
              items={items}
              onEdit={startEdit}
              onDelete={removeCategory}
              editingId={editingId}
              draft={draft}
              setDraft={setDraft}
              onSaveEdit={saveEdit}
              onCancelEdit={cancelEdit}
              onAddClick={() => setShowNew(true)}
              showNew={showNew}
              newDraft={newDraft}
              setNewDraft={setNewDraft}
              onCreate={createCategory}
              onCancelNew={() => { setShowNew(false); setNewDraft({ label: "", color: "#10B981", icon: "Tag" }); }}
              saving={saving}
            />
          ) : (
            <>
              {items.map((cat) => {
                const isEditing = editingId === cat.category_id;
                return (
                  <div
                    key={cat.category_id}
                    data-testid={`category-row-${cat.key}`}
                    className={`rounded-lg border transition ${
                      isEditing
                        ? "border-[#FACC15]/40 bg-[#FACC15]/5"
                        : "border-white/8 bg-black/30 hover:bg-black/40"
                    }`}
                  >
                    {isEditing ? (
                      <div className="p-3 space-y-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                            style={{ backgroundColor: draft.color }}
                          >
                            <CategoryIcon name={draft.icon} size={18} className="text-white" />
                          </div>
                          <input
                            data-testid={`category-edit-label-${cat.key}`}
                            value={draft.label}
                            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-heading font-bold outline-none focus:border-[#FACC15]/60"
                            placeholder="Nombre..."
                            autoFocus
                          />
                        </div>
                        <ColorPicker
                          value={draft.color}
                          onChange={(c) => setDraft({ ...draft, color: c })}
                        />
                        <IconPicker
                          value={draft.icon}
                          onChange={(i) => setDraft({ ...draft, icon: i })}
                          color={draft.color}
                        />
                        <div className="flex justify-end gap-2 pt-1">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
                          >
                            Cancelar
                          </button>
                          <button
                            data-testid={`category-save-${cat.key}`}
                            onClick={saveEdit}
                            disabled={saving}
                            className="px-3 py-1.5 rounded-lg bg-[#FACC15] hover:bg-yellow-400 disabled:opacity-40 text-black font-heading font-bold text-xs flex items-center gap-1"
                          >
                            {saving ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <Check size={12} weight="bold" />}
                            Guardar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                          style={{ backgroundColor: cat.color }}
                        >
                          <CategoryIcon name={cat.icon} size={18} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-heading font-bold text-white truncate">
                              {cat.label}
                            </div>
                            {cat.is_builtin && (
                              <span
                                className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/8 text-white/50"
                                title="Categoría base"
                              >
                                BASE
                              </span>
                            )}
                            {cat.irs_line && (
                              <span
                                data-testid={`category-irs-badge-${cat.key}`}
                                className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/40 flex items-center gap-1"
                                title={`IRS Schedule C · Línea ${cat.irs_line}`}
                              >
                                <Certificate size={9} weight="bold" /> Sch C · L{cat.irs_line}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-white/40 truncate">
                            {cat.key}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            data-testid={`category-edit-${cat.key}`}
                            onClick={() => startEdit(cat)}
                            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/12 text-white/60 hover:text-white flex items-center justify-center transition"
                            title="Editar"
                          >
                            <PencilSimple size={12} weight="bold" />
                          </button>
                          {!cat.is_builtin && (
                            <button
                              data-testid={`category-delete-${cat.key}`}
                              onClick={() => removeCategory(cat)}
                              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/60 hover:text-red-400 flex items-center justify-center transition"
                              title="Eliminar"
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* New category card */}
              {showNew ? (
                <div
                  data-testid="category-new-form"
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: newDraft.color }}
                    >
                      <CategoryIcon name={newDraft.icon} size={18} className="text-white" />
                    </div>
                    <input
                      data-testid="category-new-label"
                      value={newDraft.label}
                      onChange={(e) => setNewDraft({ ...newDraft, label: e.target.value })}
                      className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-heading font-bold outline-none focus:border-emerald-500/60"
                      placeholder="Ej: Mascotas, Gimnasio, Regalos..."
                      autoFocus
                    />
                  </div>
                  <ColorPicker value={newDraft.color} onChange={(c) => setNewDraft({ ...newDraft, color: c })} />
                  <IconPicker value={newDraft.icon} onChange={(i) => setNewDraft({ ...newDraft, icon: i })} color={newDraft.color} />
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      onClick={() => { setShowNew(false); setNewDraft({ label: "", color: "#10B981", icon: "Tag" }); }}
                      className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
                    >
                      Cancelar
                    </button>
                    <button
                      data-testid="category-new-save"
                      onClick={createCategory}
                      disabled={saving || !newDraft.label.trim()}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1"
                    >
                      {saving ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <Plus size={12} weight="bold" />}
                      Crear
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="category-add-new"
                  onClick={() => setShowNew(true)}
                  className="w-full mt-2 p-3 rounded-lg border border-dashed border-white/15 hover:border-emerald-500/40 hover:bg-emerald-500/5 text-white/50 hover:text-emerald-400 font-heading font-bold text-sm flex items-center justify-center gap-2 transition"
                >
                  <Plus size={14} weight="bold" /> Añadir categoría
                </button>
              )}
            </>
          )}
        </div>

        <div className="p-3 border-t border-white/8 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="category-reseed-builtins"
              onClick={reseedBuiltins}
              className="text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white/80"
            >
              Restaurar categorías base
            </button>
            <button
              data-testid="category-seed-irs"
              onClick={seedIRS}
              title="Agrega las 22 categorías del IRS Schedule C (Form 1040) para preparar taxes"
              className="text-[11px] font-mono uppercase tracking-widest text-amber-300/80 hover:text-amber-200 flex items-center gap-1.5"
            >
              <Certificate size={11} weight="bold" /> Sembrar IRS Schedule C
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/8 hover:bg-white/15 text-white font-heading font-bold text-sm"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1.5">
        Color
      </div>
      <div className="grid grid-cols-12 gap-1.5">
        {COLOR_CHOICES.map((c) => (
          <button
            key={c}
            data-testid={`color-swatch-${c.replace("#", "")}`}
            onClick={() => onChange(c)}
            className={`w-6 h-6 rounded-md transition ${
              value === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0F1017] scale-110" : "hover:scale-110"
            }`}
            style={{ backgroundColor: c }}
            aria-label={`Color ${c}`}
          />
        ))}
      </div>
    </div>
  );
}

function IconPicker({ value, onChange, color }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-1.5">
        Ícono
      </div>
      <div className="grid grid-cols-12 gap-1.5 max-h-32 overflow-y-auto pr-1">
        {ICON_CHOICES.map((name) => {
          const active = value === name;
          // Icon color must remain visible in BOTH themes:
          //   - light theme: modal bg is inverted to white -> use slate-500
          //   - dark theme: modal bg is #0F1017            -> use slate-300
          return (
            <button
              key={name}
              data-testid={`icon-choice-${name}`}
              onClick={() => onChange(name)}
              title={name}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition ${
                active
                  ? "ring-2 ring-[#FACC15]/60"
                  : "hover:brightness-125"
              } ${
                active ? "" : "text-slate-600 dark:text-slate-300"
              }`}
              style={{
                backgroundColor: active ? `${color}22` : "rgba(148,163,184,0.14)",
                ...(active ? { color } : {}),
              }}
            >
              <CategoryIcon
                name={name}
                size={14}
                weight={active ? "fill" : "bold"}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Grid view — launchpad-style tiles (icon + label + hover actions)
// ------------------------------------------------------------------
function CategoryGridView({
  items, onEdit, onDelete, editingId, draft, setDraft, onSaveEdit, onCancelEdit,
  onAddClick, showNew, newDraft, setNewDraft, onCreate, onCancelNew, saving,
}) {
  return (
    <div data-testid="category-grid-view" className="space-y-3">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
        {items.map((cat) => (
          <CategoryTile
            key={cat.category_id}
            cat={cat}
            editing={editingId === cat.category_id}
            draft={draft}
            setDraft={setDraft}
            onEdit={onEdit}
            onDelete={onDelete}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
            saving={saving}
          />
        ))}
        {/* Add-new tile */}
        <button
          data-testid="category-add-new-tile"
          onClick={onAddClick}
          disabled={showNew}
          className="group aspect-square flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 hover:border-emerald-500/60 hover:bg-emerald-500/8 text-white/40 hover:text-emerald-400 transition disabled:opacity-40"
        >
          <div className="w-12 h-12 rounded-2xl bg-white/6 group-hover:bg-emerald-500/25 flex items-center justify-center mb-1.5 transition">
            <Plus size={20} weight="bold" />
          </div>
          <div className="text-[11px] font-heading font-bold uppercase tracking-widest">
            Añadir
          </div>
        </button>
      </div>

      {/* Inline edit form (grid mode) */}
      {editingId && !items.find((c) => c.category_id === editingId && false) && (
        <div
          data-testid="category-edit-form-grid"
          className="mt-3 rounded-lg border border-[#FACC15]/40 bg-[#FACC15]/5 p-3 space-y-3"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FACC15]">
            Editando · {items.find((c) => c.category_id === editingId)?.label}
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: draft.color }}
            >
              <CategoryIcon name={draft.icon} size={20} className="text-white" />
            </div>
            <input
              data-testid="category-grid-edit-label"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-heading font-bold outline-none focus:border-[#FACC15]/60"
              placeholder="Nombre..."
              autoFocus
            />
          </div>
          <ColorPicker value={draft.color} onChange={(c) => setDraft({ ...draft, color: c })} />
          <IconPicker value={draft.icon} onChange={(i) => setDraft({ ...draft, icon: i })} color={draft.color} />
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onCancelEdit}
              className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              data-testid="category-grid-save"
              onClick={onSaveEdit}
              disabled={saving}
              className="on-solid px-3 py-1.5 rounded-lg bg-[#FACC15] hover:bg-yellow-400 disabled:opacity-40 text-black font-heading font-bold text-xs flex items-center gap-1"
            >
              {saving ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <Check size={12} weight="bold" />}
              Guardar
            </button>
          </div>
        </div>
      )}

      {/* Inline create form (grid mode) */}
      {showNew && (
        <div
          data-testid="category-new-form-grid"
          className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-3"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
            NUEVA CATEGORÍA
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: newDraft.color }}
            >
              <CategoryIcon name={newDraft.icon} size={20} className="text-white" />
            </div>
            <input
              data-testid="category-new-label"
              value={newDraft.label}
              onChange={(e) => setNewDraft({ ...newDraft, label: e.target.value })}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-heading font-bold outline-none focus:border-emerald-500/60"
              placeholder="Ej: Mascotas, Gimnasio, Regalos..."
              autoFocus
            />
          </div>
          <ColorPicker value={newDraft.color} onChange={(c) => setNewDraft({ ...newDraft, color: c })} />
          <IconPicker value={newDraft.icon} onChange={(i) => setNewDraft({ ...newDraft, icon: i })} color={newDraft.color} />
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onCancelNew}
              className="px-3 py-1.5 rounded-lg text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              data-testid="category-new-save"
              onClick={onCreate}
              disabled={saving || !newDraft.label.trim()}
              className="on-solid px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1"
            >
              {saving ? <ArrowClockwise size={12} weight="bold" className="animate-spin" /> : <Plus size={12} weight="bold" />}
              Crear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryTile({ cat, editing, onEdit, onDelete }) {
  return (
    <div
      data-testid={`category-tile-${cat.key}`}
      className={`group relative aspect-square flex flex-col items-center justify-center rounded-2xl transition cursor-pointer overflow-hidden ${
        editing
          ? "ring-2 ring-[#FACC15]"
          : "hover:scale-[1.03] hover:-translate-y-0.5"
      }`}
      style={{
        background: `linear-gradient(135deg, ${cat.color}22 0%, ${cat.color}0F 100%)`,
        border: `1px solid ${cat.color}55`,
      }}
      onClick={() => onEdit(cat)}
    >
      {/* Big colored icon plate */}
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2 shadow-lg"
        style={{
          backgroundColor: cat.color,
          boxShadow: `0 8px 24px -8px ${cat.color}80`,
        }}
      >
        <CategoryIcon name={cat.icon} size={26} className="text-white on-solid" style={{ color: "#fff" }} />
      </div>
      {/* Label */}
      <div className="px-2 max-w-full">
        <div className="text-[12px] font-heading font-bold text-white text-center truncate">
          {cat.label}
        </div>
      </div>
      {/* Built-in badge */}
      {cat.is_builtin && (
        <span
          className="absolute top-1.5 left-1.5 text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-black/50 text-white/60"
          title="Categoría base"
        >
          BASE
        </span>
      )}
      {/* Hover actions */}
      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          data-testid={`category-tile-edit-${cat.key}`}
          onClick={(e) => { e.stopPropagation(); onEdit(cat); }}
          className="w-6 h-6 rounded-md bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm"
          title="Editar"
        >
          <PencilSimple size={10} weight="bold" />
        </button>
        {!cat.is_builtin && (
          <button
            data-testid={`category-tile-delete-${cat.key}`}
            onClick={(e) => { e.stopPropagation(); onDelete(cat); }}
            className="w-6 h-6 rounded-md bg-black/60 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm"
            title="Eliminar"
          >
            <Trash size={10} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}
